


































































import { computed, defineComponent, PropType, ref, toRef, watch } from "@vue/composition-api";
import { TagInfo, TagInfoType } from "@scrile/api-provider/dist/api/TagsProvider";
import { Gender } from "@scrile/api-provider/dist/api/UserProvider";
import {
  PreferredGender,
  RelationshipRole,
  UserPublicInformation,
} from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import { checkRole } from "@scrile/tools/dist/lib/SecurityHelpers";
import { translateLabelsInFields } from "@/lib/formHelpers";
import { $t } from "@/lib/i18nHelpers";
import { getQuestionaryInfoFields, getValues } from "@/lib/questionaryFieldsHelper";
import { getProfileUrl } from "@/lib/urlHelpers";
import config from "@/config";
import { FormFields, FormValues } from "@/types";
import { ROLES } from "@/const";
import BaseButton from "@/components/BaseButton";
import BaseForm from "@/components/BaseForm";
import BasePage from "@/components/BasePage";
import PageTitle from "@/components/PageTitle";
import PublicInfoEdit from "@/components/PublicInfoEdit";
import SelectUserTagsModal from "@/components/SelectUserTagsModal";
import BecomeCreatorStatus from "./BecomeCreatorStatus.vue";
import ModalSocialMediaAccountsEdit from "./ModalSocialMediaAccountsEdit.vue";
import ProfileTagsView from "./ProfileTagsView.vue";
import SocialMediaAccountsView from "./SocialMediaAccountsView.vue";
import UserPictures from "./UserPictures.vue";

export default defineComponent({
  name: "PageProfileEditView",
  components: {
    UserPictures,
    BecomeCreatorStatus,
    PublicInfoEdit,
    ModalSocialMediaAccountsEdit,
    SocialMediaAccountsView,
    SelectUserTagsModal,
    ProfileTagsView,
    BaseForm,
    BasePage,
    PageTitle,
    BaseButton,
  },
  props: {
    user: { type: Object as PropType<UserPublicData> },
    info: { type: Object as PropType<Record<string, string>> },
    loading: { type: Boolean },
    saving: { type: Boolean },
    userTags: { type: Array as PropType<TagInfo[]> },
    userContentTypes: { type: Array as PropType<TagInfo[]> },
    publicInformation: { type: Object as PropType<UserPublicInformation> },
  },
  setup(props, { root, emit }) {
    const showUserTagsModal = ref(false);
    const showUserContentTypesModal = ref(false);
    const showSocialAccountsModal = ref(false);
    const isValid = ref(true);

    const isUserCreator = computed(() => checkRole(props.user?.role ?? "", [ROLES.CREATOR, ROLES.FULL_CREATOR]));

    const questionaryUserFields = computed(() => {
      const fields = getQuestionaryInfoFields().filter(
        i => !i.fieldMeta?.social && !i.fieldMeta?.amazon && !i.fieldMeta?.calendar
      );
      return translateLabelsInFields(fields);
    });

    const amazonFields = computed<FormFields>(() => {
      const fields = isUserCreator.value ? getQuestionaryInfoFields().filter(i => i.fieldMeta?.amazon) : [];
      return translateLabelsInFields(fields);
    });

    const calendarFields = computed<FormFields>(() => {
      const fields = isUserCreator.value ? getQuestionaryInfoFields().filter(i => i.fieldMeta?.calendar) : [];
      return translateLabelsInFields(fields);
    });

    const userFields = computed<FormFields>(() => {
      return [
        {
          name: "screenName",
          label: $t("Name"),
          rules: ["required", "max:30"],
          maxlength: "30",
        },
        {
          name: "username",
          label: $t("Username"),
          rules: {
            required: true,
            regex: /^[a-zA-Z0-9_.-]*$/,
            max: 30,
          },
          maxlength: "30",
          rulesMessages: {
            regex: $t("Names can only contain letters, numbers, dashes, dots, and underscores."),
          },
          hint: $t("Names can only contain letters, numbers, dashes, dots, and underscores."),
        },
        ...questionaryUserFields.value,
      ];
    });

    const userFieldsValues = ref<FormValues>({});
    const amazonValues = ref<FormValues>({});
    const calendarValues = ref<FormValues>({});
    const genderValue = ref(Gender.UNDEFINED);
    const publicInfoValues = ref({
      location: {
        city: "",
        stateCode: "",
        countryCode: "",
      },
      preferredGender: PreferredGender.UNDEFINED,
      preferredPosition: RelationshipRole.UNDEFINED,
    });

    const unwatch = watch([toRef(props, "loading")], () => {
      userFieldsValues.value = {
        screenName: props.user?.screenName,
        username: props.user?.username,
        ...getValues(questionaryUserFields.value, props.info),
      };

      amazonValues.value = getValues(amazonFields.value, props.info);
      calendarValues.value = getValues(calendarFields.value, props.info);
      genderValue.value = props.user?.gender ?? Gender.UNDEFINED;
      publicInfoValues.value = {
        location: props.publicInformation?.location ?? { city: "", stateCode: "", countryCode: "" },
        preferredGender: props.publicInformation?.preferredGender ?? PreferredGender.UNDEFINED,
        preferredPosition: props.publicInformation?.preferredPosition ?? RelationshipRole.UNDEFINED,
      };
      unwatch();
    });

    const allowThemes = computed(() => config.featureTags && isUserCreator.value);

    const onBackClick = () => {
      props.user && root.$router.push(getProfileUrl(props.user));
    };

    const onUserTagsSave = (tags: string[]) => {
      showUserTagsModal.value = false;
      emit("update:userTags", tags);
    };

    const onUserContentTypesSave = (tags: string[]) => {
      showUserContentTypesModal.value = false;
      emit("update:userContentTypes", tags);
    };

    const onValidation = (valid: boolean) => {
      isValid.value = valid;
    };

    const save = () => {
      emit("save", {
        ...userFieldsValues.value,
        ...amazonValues.value,
        ...calendarValues.value,
        publicInfoValues: publicInfoValues.value,
        gender: genderValue.value,
      });
    };

    return {
      isUserCreator,
      isValid,
      userFieldsValues,
      amazonValues,
      calendarValues,
      publicInfoValues,
      genderValue,
      userFields,
      amazonFields,
      calendarFields,
      showUserTagsModal,
      showUserContentTypesModal,
      showSocialAccountsModal,
      allowThemes,
      onBackClick,
      onUserTagsSave,
      onUserContentTypesSave,
      onValidation,
      save,
      TagInfoType,
    };
  },
});
